/**
 * Role editor — mockup "Role editor" section. Edits a draft in local state
 * (nothing is sent until Save); `LevelControl` replaces the mockup's static
 * segments, `RubyGemMark` spins in the Save button while saving, and Delete
 * uses an inline confirm panel rather than a native confirm().
 *
 * Read-only cases (banner shown, every control disabled): `role.locked`
 * (the Owner role) and `isMyRole` (nobody edits their own access — ask
 * another admin).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ADMIN_SECTION_GROUPS, levelFor, type AccessLevel, type RolePermissions,
} from '@platform/shared-types';
import { staffApi, StaffApiError, type StaffMember, type StaffRole } from '../../../services/staffApi';
import { RubyGemMark } from '../../../components/RubyGemMark';
import { MemberAvatars } from './MemberAvatars';
import { PermissionMatrix } from './PermissionMatrix';
import {
  applyGroupLevel, setLevel, diffPermissions, hasSensitiveChange, type PermissionChange,
} from './roleMath';

type V = AccessLevel | null;

interface DeleteMember { id: string; name: string; email: string }

function emptyDraft(): { name: string; description: string; perms: RolePermissions } {
  return { name: '', description: '', perms: {} };
}

export function RoleEditor({ role, myLevels, isOwner, isMyRole, staff, onSaved, onDeleted }: {
  role: StaffRole | null; // null = creating
  myLevels: RolePermissions;
  isOwner: boolean;
  isMyRole: boolean;
  /** The already-loaded staff list (RolesAccessPage) — filtered here to this role's active members. */
  staff: StaffMember[];
  onSaved: (role: StaffRole) => void;
  onDeleted: (roleId: string) => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [perms, setPerms] = useState<RolePermissions>(role?.permissions ?? {});
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteMembers, setDeleteMembers] = useState<DeleteMember[] | null>(null);
  const [duplicating, setDuplicating] = useState(false);
  // Default-open groups (mockup): the first two groups, plus the three that
  // are typically large/relevant — everything else starts collapsed.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const defaultOpen = new Set(['Inventory', 'System', 'Team']);
    ADMIN_SECTION_GROUPS.slice(0, 2).forEach((g) => defaultOpen.add(g));
    return defaultOpen;
  });
  const toggleGroup = (group: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group); else next.add(group);
      return next;
    });
  };

  // Reset the draft whenever a different role (or "new") is selected.
  useEffect(() => {
    const d = role ? { name: role.name, description: role.description ?? '', perms: role.permissions ?? {} } : emptyDraft();
    setName(d.name);
    setDescription(d.description);
    setPerms(d.perms);
    setSearch('');
    setError(null);
    setConfirmDelete(false);
    setDeleteMembers(null);
    setDeleteError(null);
  }, [role?.id]);

  const isCreating = role === null;
  const disabled = !isCreating && (!!role?.locked || isMyRole);
  const members = role ? staff.filter((s) => s.roleId === role.id && s.status !== 'terminated') : [];

  const maxFor = (sectionId: string): V => (isOwner ? 'edit' : levelFor(myLevels, sectionId));

  const savedPerms = role?.permissions ?? {};
  const changes: PermissionChange[] = useMemo(() => diffPermissions(savedPerms, perms), [savedPerms, perms]);
  const sensitive = hasSensitiveChange(changes);
  const nameChanged = name !== (role?.name ?? '');
  const descChanged = description !== (role?.description ?? '');
  const dirty = isCreating ? name.trim().length > 0 || changes.length > 0 : changes.length > 0 || nameChanged || descChanged;

  const query = search.trim().toLowerCase();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isCreating) {
        const res = await staffApi.roles.create({ name: name.trim(), description: description.trim() || undefined, permissions: perms });
        onSaved(res.role);
      } else {
        const res = await staffApi.roles.update(role.id, { name, description, permissions: perms });
        onSaved(res.role);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not save the role');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    const d = role ? { name: role.name, description: role.description ?? '', perms: role.permissions ?? {} } : emptyDraft();
    setName(d.name);
    setDescription(d.description);
    setPerms(d.perms);
    setError(null);
  };

  const duplicate = async () => {
    if (!role) return;
    setDuplicating(true);
    setError(null);
    try {
      const res = await staffApi.roles.create({
        name: `${role.name} copy`,
        description: role.description,
        permissions: role.permissions,
      });
      onSaved(res.role);
    } catch (e: any) {
      setError(e?.message || 'Could not duplicate the role');
    } finally {
      setDuplicating(false);
    }
  };

  const confirmDeleteRole = async () => {
    if (!role) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await staffApi.roles.remove(role.id);
      setConfirmDelete(false);
      onDeleted(role.id);
    } catch (e: any) {
      if (e instanceof StaffApiError && e.status === 409 && Array.isArray(e.body?.members)) {
        setDeleteMembers(e.body.members);
      } else {
        setDeleteError(e?.message || 'Could not delete the role');
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <section className="bg-white text-stone-900 rounded-xl border border-stone-200 min-w-0">
      <div className="p-4 border-b border-stone-200 flex flex-wrap gap-3 items-start justify-between">
        <div className="min-w-0 flex-1">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={disabled}
            placeholder="Role name"
            className="text-lg font-semibold border-b border-transparent hover:border-stone-300 focus:border-[#DC143C] outline-none w-full bg-transparent disabled:text-stone-500"
          />
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={disabled}
            placeholder="What is this role for?"
            className="text-sm text-stone-500 outline-none w-full mt-0.5 bg-transparent disabled:text-stone-400"
          />
          {!isCreating && (
            <div className="flex items-center gap-1 mt-2">
              <MemberAvatars members={members} />
              <span className="text-xs text-stone-500">{role?.staffCount ?? members.length} member{(role?.staffCount ?? members.length) === 1 ? '' : 's'}</span>
              <button type="button" onClick={() => navigate('/staff')} className="ml-2 text-xs text-[#DC143C] font-medium min-h-[44px]">
                Manage members
              </button>
            </div>
          )}
        </div>
        {!isCreating && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={duplicate}
              disabled={duplicating}
              className="min-h-[44px] px-3 rounded-lg border border-stone-300 text-sm disabled:opacity-60"
            >
              {duplicating ? 'Duplicating…' : 'Duplicate'}
            </button>
            {!role?.locked && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="min-h-[44px] px-3 rounded-lg border border-stone-300 text-sm text-red-600"
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>

      {disabled && (
        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm">
          {role?.locked
            ? "The Owner role always has everything and can't be changed."
            : 'This is your role. Ask another admin to change it.'}
        </div>
      )}

      <div className="px-4 py-3 flex flex-wrap items-center gap-3 border-b border-stone-100 bg-stone-50">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search sections…"
          className="flex-1 min-w-[180px] min-h-[44px] px-3 rounded-lg border border-stone-300 text-sm outline-none focus:border-[#DC143C]"
        />
        <div className="flex flex-wrap gap-3 text-xs text-stone-600">
          <span><b className="text-stone-700">Off</b> hidden &amp; blocked</span>
          <span><b className="text-sky-800">View</b> open and read only</span>
          <span><b className="text-[#9F1239]">Edit</b> read and change everything</span>
        </div>
      </div>

      <PermissionMatrix
        perms={perms}
        query={query}
        openGroups={openGroups}
        onToggleGroup={toggleGroup}
        maxFor={maxFor}
        disabled={disabled}
        onSetLevel={(id, v) => setPerms((p) => setLevel(p, id, v))}
        onSetGroupLevel={(group, v) => setPerms((p) => applyGroupLevel(p, group, v, maxFor))}
      />

      <div className="p-4 border-t border-stone-200 bg-stone-50/60">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-stone-500">GitHub repositories</p>
            <p className="text-xs text-stone-400">Coming later — grant this role access to specific repos. Applied through GitHub by the backend.</p>
          </div>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-stone-200 text-stone-500">Later</span>
        </div>
      </div>

      <div className="sticky bottom-0 p-4 border-t border-stone-200 bg-white rounded-b-xl flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px] text-sm">
          {error && <p className="text-red-600 text-sm mb-1">{error}</p>}
          <p className="font-medium text-stone-900">
            {isCreating ? (dirty ? 'Ready to create' : 'Fill in a name to create this role') : `${changes.length} unsaved change${changes.length === 1 ? '' : 's'}`}
          </p>
          {!isCreating && changes.length > 0 && (
            <ul className="text-xs mt-0.5 space-y-0.5">
              {changes.map((c) => (
                <li key={c.sectionId} className={c.sensitive ? 'text-[#9F1239]' : 'text-stone-600'}>
                  {c.label}: {c.from ?? 'Off'} → {c.to ?? 'Off'}
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-stone-500 mt-1">
            {isCreating
              ? 'Every super admin is emailed and it’s recorded in the Access log.'
              : `Takes effect within a minute for ${role?.staffCount ?? 0} member${(role?.staffCount ?? 0) === 1 ? '' : 's'}. Every super admin is emailed and it's recorded in the Access log.${sensitive ? ' This change touches a sensitive section.' : ''}`}
          </p>
        </div>
        {!isCreating && (
          <button type="button" onClick={discard} disabled={!dirty || saving} className="min-h-[44px] px-4 rounded-lg border border-stone-300 text-sm disabled:opacity-50">
            Discard
          </button>
        )}
        <button
          type="button"
          onClick={save}
          disabled={disabled || saving || !dirty || (isCreating && !name.trim())}
          className="min-h-[44px] px-5 rounded-lg bg-[#DC143C] hover:bg-[#9F1239] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
        >
          {saving && <RubyGemMark size={16} drawing color="currentColor" />}
          {isCreating ? 'Create role' : 'Save role'}
        </button>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !deleteBusy && setConfirmDelete(false)}>
          <div className="w-full max-w-md bg-white text-stone-900 rounded-xl shadow-xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-stone-900">Delete "{role?.name}"?</h3>
            {deleteMembers && deleteMembers.length > 0 ? (
              <div className="text-sm text-stone-600 space-y-2">
                <p>This role still has {deleteMembers.length} member{deleteMembers.length === 1 ? '' : 's'}. Move them to another role first:</p>
                <ul className="list-disc pl-5">
                  {deleteMembers.map((m) => <li key={m.id}>{m.name} ({m.email})</li>)}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-stone-600">This can't be undone.</p>
            )}
            {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmDelete(false)} className="min-h-[44px] px-4 rounded-lg border border-stone-300 text-stone-700">
                Cancel
              </button>
              {!(deleteMembers && deleteMembers.length > 0) && (
                <button
                  type="button"
                  onClick={confirmDeleteRole}
                  disabled={deleteBusy}
                  className="min-h-[44px] px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {deleteBusy && <RubyGemMark size={16} drawing color="currentColor" />} Delete role
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default RoleEditor;
